import { createContext, PropsWithChildren, useMemo, useState } from 'react';

import { AxiosError } from 'axios';

import { K8sProvider } from '@cast/shared-types';

import { useAbility } from '@cast/shared-ability-data-access';
import { useFilteredProvidersForOrganization } from 'hooks/useFilteredProvidersForOrganization';

import {
  COMMITMENT_TAB_TO_CLOUD_SERVICES,
  COMMITMENT_TAB_TO_PROVIDER,
  PROVIDER_TO_COMMITMENT_TABS,
} from '../_constants';
import { useCommitmentsViewMode } from '../_hooks/useCommitmentsViewMode';
import {
  NormalizedCommitments,
  useNormalizedCommitments,
} from '../_hooks/useNormalizedCommitments';
import { CommitmentsViewMode, CommitmentTab } from '../_types';
import { CommitmentProvider, NormalizedCommitment } from '../types';

type CommitmentsContextState = {
  commitmentsViewMode: CommitmentsViewMode;
  availableTabs: CommitmentTab[];
  activeTab: CommitmentTab;
  setActiveTab: (tab: CommitmentTab) => void;
  activeProvider: CommitmentProvider;
  isMultiCloud: boolean;
  isLoading: boolean;
  refetch: () => void;
  error: AxiosError | null;
  activeTabCommitments: NormalizedCommitment[];
  tabCommitmentCounts: Record<CommitmentTab, number>;
  hasCommitments: boolean;
};

const getCommitmentsForTab = ({
  tab,
  normalizedCommitments,
}: {
  tab: CommitmentTab;
  normalizedCommitments: NormalizedCommitments;
}): NormalizedCommitment[] => {
  const cloudServices = COMMITMENT_TAB_TO_CLOUD_SERVICES[tab];
  if (Array.isArray(cloudServices)) {
    return cloudServices.flatMap(
      (cloudService) => normalizedCommitments[cloudService]
    );
  }
  return normalizedCommitments[cloudServices];
};

export const CommitmentsContext = createContext<CommitmentsContextState>(
  {} as never
);

export const CommitmentsProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  const isAzureSpEnabled = useAbility().can('view', 'FeatureAzureSavingsPlans');
  const commitmentsViewMode = useCommitmentsViewMode();
  const availableProviders: CommitmentProvider[] =
    useFilteredProvidersForOrganization([
      // Note: the order of providers is important - it determines the order of tabs in the UI
      K8sProvider.EKS,
      K8sProvider.GKE,
      K8sProvider.AKS,
    ]);

  const availableTabs: CommitmentTab[] = availableProviders
    .flatMap((provider) => PROVIDER_TO_COMMITMENT_TABS[provider])
    .filter((tab) => tab !== CommitmentTab.AZURE_SP || isAzureSpEnabled);

  const isMultiCloud = availableProviders.length > 1;

  const { normalizedData, isLoading, refetch, error } =
    useNormalizedCommitments();

  const [_activeTab, setActiveTab] = useState<CommitmentTab>();

  const activeTab = useMemo(() => {
    return (
      _activeTab ||
      // Select first tab with commitments if available
      availableTabs.find(
        (tab) =>
          getCommitmentsForTab({ tab, normalizedCommitments: normalizedData })
            .length > 0
      ) ||
      CommitmentTab.GCP_RESOURCE_CUD
    );
  }, [_activeTab, availableTabs, normalizedData]);

  const activeTabCommitments = useMemo(
    () =>
      getCommitmentsForTab({
        tab: activeTab,
        normalizedCommitments: normalizedData,
      }),
    [activeTab, normalizedData]
  );

  const tabCommitmentCounts = useMemo(() => {
    return Object.values(CommitmentTab).reduce((acc, tab) => {
      acc[tab] = getCommitmentsForTab({
        tab,
        normalizedCommitments: normalizedData,
      }).length;
      return acc;
    }, {} as Record<CommitmentTab, number>);
  }, [normalizedData]);

  const hasCommitments =
    !isLoading && Object.values(tabCommitmentCounts).some((count) => !!count);

  return (
    <CommitmentsContext.Provider
      value={{
        availableTabs,
        activeProvider: COMMITMENT_TAB_TO_PROVIDER[activeTab],
        activeTab,
        setActiveTab,
        isMultiCloud,
        isLoading,
        activeTabCommitments,
        refetch,
        error,
        tabCommitmentCounts,
        hasCommitments,
        commitmentsViewMode,
      }}
    >
      {children}
    </CommitmentsContext.Provider>
  );
};
